import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Sparkles,
} from "lucide-react";
import FloatingHalloween from "@/components/FloatingHalloween";

export const Route = createFileRoute("/library/halloween-grounding")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "A Gentle Halloween Grounding — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A soft, trauma-informed Halloween grounding practice. Pumpkins, ghosts and autumn leaves as calming visuals — nothing scary, everything optional, and you can leave at any point.",
      },
      { property: "og:title", content: "A Gentle Halloween Grounding — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Come back to the present with pumpkins, ghosts and falling leaves. Nothing scary, everything optional.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/library/halloween-grounding" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/library/halloween-grounding" }],
  }),
  component: withMemberGate(HalloweenGroundingPage, "Recovery Library"),
});

// ────────────────────────────────────────────────────────────────────
// Gentle shapes — the same warm, soft silhouettes used elsewhere on the
// site. Nothing grinning, nothing frightening.
// ────────────────────────────────────────────────────────────────────

function PumpkinSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        d="M12 5.4c2 0 2.6 1 3.4 1 2.9 0 5 2.9 5 6.6S17.6 21 12 21 3.6 16.7 3.6 13 5.7 6.4 8.6 6.4c.8 0 1.4-1 3.4-1z"
        fill="currentColor"
      />
      <path
        d="M12 5.6c0-1.6.6-2.7 2-3.4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function GhostSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        d="M12 2.6c3.6 0 6.2 2.8 6.2 6.6v11.2l-2.1-1.6-2 1.6-2.1-1.6-2.1 1.6-2-1.6-2.1 1.6V9.2C5.8 5.4 8.4 2.6 12 2.6z"
        fill="currentColor"
      />
        <circle cx="9.8" cy="9.4" r="0.9" fill="var(--background, #fff8ee)" />
        <circle cx="14.2" cy="9.4" r="0.9" fill="var(--background, #fff8ee)" />
    </svg>
  );
}

function LeafSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        d="M20 3.6c0 8.2-4.6 13-10 13.6-2.6.3-4.6-.9-5.4-2.9C3 10.6 8.4 4.7 20 3.6zM9 16.4 4 21.4"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Tiny drifting backdrop used behind the intro and closing cards. */
function MiniDrift({ tone }: { tone: string }) {
  const items = [
    { left: 10, size: 18, delay: 0, dur: 26, drift: 14, opacity: 0.5, kind: "pumpkin" as const },
    { left: 28, size: 15, delay: 7, dur: 22, drift: -18, opacity: 0.45, kind: "leaf" as const },
    { left: 47, size: 16, delay: 14, dur: 28, drift: 12, opacity: 0.4, kind: "ghost" as const },
    { left: 66, size: 14, delay: 4, dur: 24, drift: 16, opacity: 0.45, kind: "leaf" as const },
    { left: 84, size: 17, delay: 19, dur: 30, drift: -12, opacity: 0.5, kind: "pumpkin" as const },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
      {items.map((it, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          aria-hidden
          className="halloween-drift absolute bottom-[-28px]"
          style={
            {
              left: `${it.left}%`,
              width: it.size,
              height: it.size,
              color: tone,
              animationDelay: `-${it.delay}s`,
              "--drift-dur": `${it.dur}s`,
              "--drift-opacity": it.opacity,
              "--star-drift": `${it.drift}px`,
            } as React.CSSProperties
          }
        >
          {it.kind === "pumpkin" && <PumpkinSvg className="h-full w-full" />}
          {it.kind === "ghost" && <GhostSvg className="h-full w-full" />}
          {it.kind === "leaf" && <LeafSvg className="h-full w-full" />}
        </svg>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Breathing pacer — ghost gently rises as you breathe in and settles as
// you breathe out. Plain JS phases so the on-screen words always match
// the movement, with a pause button and a reduced-motion fallback.
// ────────────────────────────────────────────────────────────────────

type Phase = "in" | "hold" | "out";
const PHASE_MS: Record<Phase, number> = { in: 4000, hold: 2000, out: 6000 };
const PHASE_LABEL: Record<Phase, string> = {
  in: "Breathe in, slowly",
  hold: "Hold, gently",
  out: "Breathe out, longer",
};
const NEXT_PHASE: Record<Phase, Phase> = { in: "hold", hold: "out", out: "in" };

function GhostBreath() {
  // Read the reduced-motion preference after mount — during SSR there is
  // no window, and the pacer still works (words only) before hydration.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [phase, setPhase] = useState<Phase>("in");
  const [paused, setPaused] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    timer.current = window.setTimeout(() => setPhase(NEXT_PHASE[phase]), PHASE_MS[phase]);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [phase, paused]);

  // Clean up the timer if the component unmounts mid-breath.
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const ghostScale = phase === "in" ? 1.18 : phase === "hold" ? 1.18 : 1;

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="relative flex h-48 w-48 items-center justify-center rounded-full bg-gradient-to-b from-amber-100/70 via-orange-50/60 to-transparent"
        role="img"
        aria-label={`Breathing pacer. ${PHASE_LABEL[phase]}.`}
      >
        <GhostSvg
          className={`h-24 w-24 text-slate-300 transition-transform ease-in-out ${reducedMotion ? "" : "motion-safe:transition-transform"}`}
          // The movement is driven by the phase, so reduced-motion users still
          // see the words change even though we keep the ghost still.
        />
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            transform: `scale(${ghostScale})`,
            transition: `transform ${reducedMotion ? 0 : PHASE_MS[phase]}ms ease-in-out`,
            background:
              "radial-gradient(circle, color-mix(in oklab, var(--gold) 22%, transparent) 0%, transparent 70%)",
          }}
        />
      </div>
      <p aria-live="polite" className="font-display text-lg text-foreground">
        {PHASE_LABEL[phase]}
      </p>
      <button
        type="button"
        onClick={() => setPaused((p) => !p)}
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold/40 bg-card px-4 text-sm font-medium text-foreground transition-colors hover:bg-gold-light/30"
        aria-pressed={paused}
      >
        {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        {paused ? "Continue" : "Pause"}
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Steps
// ────────────────────────────────────────────────────────────────────

type Step = {
  key: string;
  title: string;
  visual: "drift" | "breath" | "pumpkin" | "leaves" | "ghost-sounds" | "closing";
  body: string[];
  prompt?: string;
};

const STEPS: Step[] = [
  {
    key: "settle",
    title: "Arrive, just as you are",
    visual: "drift",
    body: [
      "Find a spot where you feel comfortable — a chair, a bed, a corner you like.",
      "If it feels okay, let your shoulders drop and your hands rest somewhere warm.",
      "You don't have to do this perfectly. There's no pass or fail here — and if a step doesn't suit you, you can skip it or stop altogether.",
    ],
  },
  {
    key: "breath",
    title: "Breathe with the little ghost",
    visual: "breath",
    body: [
      "A soft ghost is going to rise and settle with your breath. It rises as you breathe in, waits a moment, and settles as you breathe out.",
      "Try letting your out-breath be a little longer than your in-breath — that's what tells your body it's safe to slow down.",
      "Following it is enough. If your mind wanders off, that's completely normal — just come back to the next word.",
    ],
  },
  {
    key: "pumpkin-colours",
    title: "Name three pumpkin colours",
    visual: "pumpkin",
    body: [
      "Look around the room you're in, softly — no rushing.",
      "Name three things you can see that carry a pumpkin colour: orange, amber, gold, warm brown. They can be tiny — a mug, a lampshade, the light on the wall.",
      "Say each one in your head, or out loud if you like: “That one is warm-coloured. So is that.”",
    ],
    prompt: "Colour one: …  Colour two: …  Colour three: …",
  },
  {
    key: "leaf-touch",
    title: "Four things touching you, like settling leaves",
    visual: "leaves",
    body: [
      "Bring your attention to your body now.",
      "Notice four things touching you right now — the chair holding you, socks on your feet, a sleeve on your arm, the air on your face. Picture each one as a leaf landing gently.",
      "You don't have to change anything. Just noticing is the practice.",
    ],
    prompt: "Touch one: …  Touch two: …  Touch three: …  Touch four: …",
  },
  {
    key: "ghost-sounds",
    title: "Three gentle sounds",
    visual: "ghost-sounds",
    body: [
      "Listen out for three sounds, near or far. A soft ghost drifting by would hear them too.",
      "Maybe a radiator, traffic, a bird, your own breathing. Faraway sounds count just as much as close ones.",
      "Let each sound arrive and pass, like the ghost drifting through — you don't have to hold on to any of them.",
    ],
    prompt: "Sound one: …  Sound two: …  Sound three: …",
  },
  {
    key: "closing",
    title: "You're here",
    visual: "closing",
    body: [
      "That's the whole practice — colours, touch, sound, breath. You just came back to the present, in your own time.",
      "If it helped, this page will stay in your Recovery Library all season, and the everyday grounding practices are always there too.",
      "And if any big feelings came up along the way, that's okay — be gentle with yourself, and take any next step that feels kind.",
    ],
  },
];

function StepVisual({ step }: { step: Step }) {
  switch (step.visual) {
    case "drift":
      return (
        <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-gold-light/40 via-cream/60 to-card">
          <MiniDrift tone="var(--gold-deep, #b8860b)" />
          <PumpkinSvg className="relative h-20 w-20 text-[var(--gold-deep)] opacity-80" />
        </div>
      );
    case "breath":
      return <GhostBreath />;
    case "pumpkin":
      return (
        <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-amber-100/60 via-orange-50/50 to-card">
          <div
            aria-hidden
            className="absolute h-36 w-36 rounded-full"
            style={{
              background:
                "radial-gradient(circle, color-mix(in oklab, var(--gold) 30%, transparent) 0%, transparent 70%)",
            }}
          />
          <PumpkinSvg className="relative h-24 w-24 text-orange-400/90" />
        </div>
      );
    case "leaves": {
      const leaves = [
        { left: 18, delay: 0, size: 26, drift: -14 },
        { left: 40, delay: 1.2, size: 20, drift: 16 },
        { left: 62, delay: 2.1, size: 24, drift: -12 },
        { left: 82, delay: 0.6, size: 18, drift: 12 },
      ];
      return (
        <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-emerald-50/70 via-cream/60 to-card">
          {leaves.map((l, i) => (
            <svg
              key={i}
              viewBox="0 0 24 24"
              aria-hidden
              className="halloween-drift absolute bottom-[-30px] text-emerald-500/70"
              style={
                {
                  left: `${l.left}%`,
                  width: l.size,
                  height: l.size,
                  animationDelay: `-${l.delay}s`,
                  "--drift-dur": "18s",
                  "--drift-opacity": 0.7,
                  "--star-drift": `${l.drift}px`,
                } as React.CSSProperties
              }
            >
              <LeafSvg className="h-full w-full" />
            </svg>
          ))}
          <span className="sr-only">Four leaves drifting gently downward</span>
        </div>
      );
    }
    case "ghost-sounds":
      return (
        <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-sky-50/70 via-cream/60 to-card">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden
              className="motion-safe:animate-ping absolute rounded-full border border-sky-300/50"
              style={{
                width: 72 + i * 44,
                height: 72 + i * 44,
                animationDuration: "3.6s",
                animationDelay: `${i * 1.1}s`,
              }}
            />
          ))}
          <GhostSvg className="relative h-20 w-20 text-slate-300" />
        </div>
      );
    case "closing":
      return (
        <div className="relative flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-gold-light/50 via-cream/60 to-card">
          <MiniDrift tone="var(--gold-deep, #b8860b)" />
          <div
            aria-hidden
            className="absolute h-32 w-32 rounded-full"
            style={{
              background:
                "radial-gradient(circle, color-mix(in oklab, var(--gold) 35%, transparent) 0%, transparent 70%)",
            }}
          />
          <div className="relative flex flex-col items-center">
            <PumpkinSvg className="h-16 w-16 text-[var(--gold-deep)]" />
            <Sparkles className="mt-1 h-5 w-5 text-[var(--gold-deep)]" />
          </div>
        </div>
      );
  }
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

function HalloweenGroundingPage() {
  const [stepIndex, setStepIndex] = useState(0);
  const [done, setDone] = useState(false);
  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const cardRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback((next: number) => {
    setStepIndex(Math.min(Math.max(next, 0), STEPS.length - 1));
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const finish = useCallback(() => {
    setDone(true);
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const progress = useMemo(
    () => (done ? 100 : Math.round(((stepIndex + 1) / STEPS.length) * 100)),
    [stepIndex, done],
  );

  return (
    <div className="relative mx-auto max-w-2xl px-4 py-8 md:py-12">
      <FloatingHalloween />

      <Link
        to="/library"
        className="mb-6 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to the Recovery Library
      </Link>

      <header className="relative mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-card px-3 py-1 text-xs text-muted-foreground">
          🎃 Seasonal practice · Recovery Library
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground md:text-4xl">
          A Gentle Halloween Grounding
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Pumpkins, soft little ghosts and falling leaves — as calm, cosy things to come back
          to the present with. Nothing here is scary, nothing is a test, and you can stop at
          any point.
        </p>
      </header>

      <div ref={cardRef} className="scroll-mt-24">
        {done ? (
          <section
            aria-label="Practice complete"
            className="rounded-3xl border border-gold/30 bg-gradient-to-b from-gold-light/30 via-card to-card p-6 text-center shadow-[var(--shadow-soft-lg)] md:p-10"
          >
            <div className="relative mx-auto mb-6 flex h-44 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-b from-gold-light/40 via-cream/60 to-card">
              <MiniDrift tone="var(--gold-deep, #b8860b)" />
              <GhostSvg className="relative h-20 w-20 text-slate-300" />
            </div>
            <h2 className="font-display text-2xl text-foreground">Well done — you came back to now.</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
              However far you got, that counted. You're welcome to run through it again any
              time, or try one of these instead:
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  setDone(false);
                  setStepIndex(0);
                }}
                className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-background transition-transform hover:scale-[1.02]"
              >
                Do it again
              </button>
              <Link
                to="/grounding"
                className="inline-flex min-h-12 items-center gap-2 rounded-full border border-gold/40 bg-card px-6 text-sm font-semibold text-foreground transition-colors hover:bg-gold-light/30"
              >
                More grounding practices
              </Link>
              <Link
                to="/journal"
                className="inline-flex min-h-12 items-center gap-2 rounded-full border border-gold/40 bg-card px-6 text-sm font-semibold text-foreground transition-colors hover:bg-gold-light/30"
              >
                Write about how it felt
              </Link>
            </div>
          </section>
        ) : (
          <section
            aria-label={`Step ${stepIndex + 1} of ${STEPS.length}: ${step.title}`}
            className="overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-b from-gold-light/20 via-card to-card shadow-[var(--shadow-soft-lg)]"
          >
            <div className="p-5 md:p-8">
              <StepVisual step={step} />

              <div className="mt-6 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  Step {stepIndex + 1} of {STEPS.length}
                </p>
                <h2 className="mt-2 font-display text-xl text-foreground md:text-2xl">
                  {step.title}
                </h2>
              </div>

              <div className="mt-4 space-y-3 text-center">
                {step.body.map((p, i) => (
                  <p key={i} className="mx-auto max-w-md text-sm leading-relaxed text-muted-foreground md:text-base">
                    {p}
                  </p>
                ))}
                {step.prompt && (
                  <p className="mx-auto max-w-md rounded-2xl border border-dashed border-gold/40 bg-cream/50 px-4 py-3 text-sm italic text-foreground">
                    {step.prompt}
                  </p>
                )}
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {isLast ? (
                  <button
                    type="button"
                    onClick={finish}
                    className="inline-flex min-h-12 w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-background transition-transform hover:scale-[1.02]"
                  >
                    <Sparkles className="h-4 w-4" /> Finish gently
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => goTo(stepIndex + 1)}
                    className="inline-flex min-h-12 w-full max-w-xs items-center justify-center gap-2 rounded-full bg-[var(--gold-deep)] px-6 text-sm font-semibold text-background transition-transform hover:scale-[1.02]"
                  >
                    I'm ready for the next one <ChevronRight className="h-4 w-4" />
                  </button>
                )}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {stepIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => goTo(stepIndex - 1)}
                      className="inline-flex min-h-10 items-center gap-1 hover:text-foreground"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" /> Back
                    </button>
                  )}
                  {!isLast && (
                    <button
                      type="button"
                      onClick={() => goTo(stepIndex + 1)}
                      className="inline-flex min-h-10 items-center gap-1 hover:text-foreground"
                    >
                      Skip this step <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {stepIndex > 0 && !isLast && <span aria-hidden>·</span>}
                  <button
                    type="button"
                    onClick={finish}
                    className="inline-flex min-h-10 items-center hover:text-foreground"
                  >
                    Stop for now
                  </button>
                </div>
              </div>
            </div>

            {/* Progress dots */}
            <div
              className="flex items-center justify-center gap-2 border-t border-gold/15 bg-cream/40 px-5 py-4"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Practice progress"
            >
              {STEPS.map((s, i) => (
                <span
                  key={s.key}
                  aria-hidden
                  className={`h-2 rounded-full transition-all ${
                    i < stepIndex
                      ? "w-2 bg-[var(--gold-deep)]"
                      : i === stepIndex
                        ? "w-6 bg-[var(--gold-deep)]"
                        : "w-2 bg-gold/25"
                  }`}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      <p className="mx-auto mt-8 max-w-md text-center text-xs leading-relaxed text-muted-foreground">
        This is a wellbeing practice, not therapy or medical advice. If you're in crisis or
        feel unsafe right now, please use the support page — it's always one tap away.
      </p>
    </div>
  );
}
